import { NotificationType } from '../../notification-center/types';
import { TransactionEntryType } from '../utils/transactionTypesUtils';

export const transactionTypeByTypeName = {
  BuyTransaction: 'BUY',
  SellTransaction: 'SELL',
  GiveawayTransaction: 'GIVEAWAY',
  StakingRewardTransaction: 'STAKING_REWARD',
  MergerCryptoTransaction: 'MERGER_CRYPTO',
  DeductionTransaction: 'DEDUCTION',
  SwapTransaction: 'SWAP',
  OtcTransaction: 'OTC',
  StakeStakingTransaction: 'STAKE',
  UnstakeStakingTransaction: 'UNSTAKE',
  StakeTransaction: 'STAKE',
  UnstakeTransaction: 'UNSTAKE',
  SavingsPlanTransaction: 'SAVINGS_PLAN',
  InternalSavingsPlanTransaction: 'INTERNAL_SAVINGS_PLAN',
  SavingsPlanFinished: 'SAVINGS_PLAN_FINISHED',
  SavingsPlanFailedMergerCrypto: 'SAVINGS_PLAN_FAILED_MERGER_CRYPTO',
  SavingsPlanFailedMergerStock: 'SAVINGS_PLAN_FAILED_MERGER_STOCK',
  SavingsPlanFailedDelisting: 'SAVINGS_PLAN_FAILED_DELISTING',
  SavingsPlanFailedMergerCash: 'SAVINGS_PLAN_FAILED_MERGER_CASH',
  SavingsPlanFailedInPartnerApp: 'SAVINGS_PLAN_FAILED_IN_PARTNER_APP',
  DepositTransaction: 'DEPOSIT',
  DepositTransactionFiatTransfer: 'FIAT_DEPOSIT',
  WithdrawalTransactionFiatTransfer: 'FIAT_WITHDRAWAL',
  DelistingTransaction: 'DELISTING',
  DividendTransaction: 'DIVIDEND',
  BaseTimelineTransaction: 'BASE_TIMELINE',
  MergerCashTransaction: 'MERGER_CASH',
  MergerStockTransaction: 'MERGER_STOCK',
  MetalStorageFeeTransaction: 'METAL_STORAGE_FEE',
  StockSplitTransaction: 'STOCK_SPLIT',
  ReverseStockSplitTransaction: 'REVERSE_STOCK_SPLIT',
  SpinOffTransaction: 'SPIN_OFF',
  TradeTransaction: 'TRADE',
  TaxRefundTransaction: 'TAX_REFUND',
  WithdrawalTransaction: 'WITHDRAWAL',
  WithdrawalRefundTransaction: 'WITHDRAWAL_REFUNDED',
  WithdrawalTransactionRejected: 'WITHDRAWAL_REJECTED',
  Transaction: 'TRANSACTION',
  TenderTransaction: 'TENDER',
  RightsIssueTransaction: 'RIGHTS_ISSUE',
  WithdrawalCryptoTransfersTransaction: 'WITHDRAWAL_CRYPTO_TRANSFERS',
  DepositCryptoTransfersTransaction: 'DEPOSIT_CRYPTO_TRANSFERS',
  BuyOrderTransaction: 'ORDER_BUY',
  SellOrderTransaction: 'ORDER_SELL',
  RefundTransaction: 'REFUND',
} as const;

export const transactionTypeByNotificationType: Partial<
  Record<NotificationType, TransactionTypes>
> = {
  WITHDRAWAL_REJECTED: 'WITHDRAWAL_CRYPTO_TRANSFERS',
  WITHDRAWAL_CANCELED: 'WITHDRAWAL_CRYPTO_TRANSFERS',
  WITHDRAWAL_FAILED: 'WITHDRAWAL_CRYPTO_TRANSFERS',
  WITHDRAWAL_FINISHED: 'WITHDRAWAL_CRYPTO_TRANSFERS',
  WITHDRAWAL_ON_HOLD: 'WITHDRAWAL_CRYPTO_TRANSFERS',
  DEPOSIT_CANCELED: 'DEPOSIT_CRYPTO_TRANSFERS',
  DEPOSIT_FAILED: 'DEPOSIT_CRYPTO_TRANSFERS',
  DEPOSIT_FINISHED: 'DEPOSIT_CRYPTO_TRANSFERS',
  DEPOSIT_ON_HOLD: 'DEPOSIT_CRYPTO_TRANSFERS',
  DEPOSIT_PENDING: 'DEPOSIT_CRYPTO_TRANSFERS',
  DEPOSIT_REJECTED: 'DEPOSIT_CRYPTO_TRANSFERS',
  // Without this the drawer resolves an undefined type and falls back to the
  // generic trade query (trade.not.found). The fiat query needs 'FIAT_DEPOSIT'.
  FIAT_DEPOSIT_FAILURE: 'FIAT_DEPOSIT',
};

type Keys = keyof typeof transactionTypeByTypeName;

type Values = (typeof transactionTypeByTypeName)[Keys];

const transactionTypeNamesByType = Object.fromEntries(
  Object.entries(transactionTypeByTypeName).map(([key, value]) => [value, key]),
) as Record<Values, Keys>;

export const getTransactionTypeNameByType = (type: string) => {
  return transactionTypeNamesByType[type as Values];
};

export const getTransactionTypeNameByNotificationType = (
  type: NotificationType,
) => {
  return transactionTypeByNotificationType[type];
};

export type TransactionTypes =
  (typeof transactionTypeByTypeName)[keyof typeof transactionTypeByTypeName];

export const transactionTagTranslationsByLocalType = {
  // the translation key for this has kind of a wrong name
  [transactionTypeByTypeName.OtcTransaction]: 'tx_tag_otc_buy',
  [transactionTypeByTypeName.SpinOffTransaction]: 'tx_tag_spinoff',
  // [transactionTypeByTypeName.TenderTransaction]: 'tx_tag_tender',
  [transactionTypeByTypeName.StockSplitTransaction]: 'tx_tag_stocksplit',
  // [transactionTypeByTypeName.RightsIssueTransaction]: 'tx_tag_rightissue',
  [transactionTypeByTypeName.ReverseStockSplitTransaction]:
    'tx_tag_reversestocksplit',
  [transactionTypeByTypeName.MergerStockTransaction]: 'tx_tag_mergerstock',
  [transactionTypeByTypeName.MergerCashTransaction]: 'tx_tag_mergercash',
  [transactionTypeByTypeName.MergerCryptoTransaction]: 'tx_tag_mergercrypto',
  [transactionTypeByTypeName.DividendTransaction]: 'tx_tag_dividend',
  [transactionTypeByTypeName.DelistingTransaction]: 'tx_tag_delisting',
  [transactionTypeByTypeName.SavingsPlanTransaction]: 'savings_plan',
  [transactionTypeByTypeName.InternalSavingsPlanTransaction]: 'savings_plan',
  [transactionTypeByTypeName.SavingsPlanFailedMergerCrypto]: 'savings_plan',
  [transactionTypeByTypeName.SavingsPlanFailedMergerStock]: 'savings_plan',
  [transactionTypeByTypeName.SavingsPlanFailedDelisting]: 'savings_plan',
  [transactionTypeByTypeName.SavingsPlanFailedMergerCash]: 'savings_plan',
  [transactionTypeByTypeName.SavingsPlanFailedInPartnerApp]: 'savings_plan',
  [transactionTypeByTypeName.WithdrawalCryptoTransfersTransaction]:
    'tx_details_type_withdrawal',
  [transactionTypeByTypeName.DepositCryptoTransfersTransaction]: 'deposit',
  [transactionTypeByTypeName.StakeStakingTransaction]: 'stake',
};

export const transactionItemTranslations = {
  otcTransaction: 'tx_tag_otc_buy',
  spinOffTransaction: 'tx_tag_spinoff',
  stockSplitTransaction: 'tx_tag_stocksplit',
  reverseStockSplitTransaction: 'tx_tag_reversestocksplit',
  mergerStockTransaction: 'tx_tag_mergerstock',
  mergerCashTransaction: 'tx_tag_mergercash',
  mergerCryptoTransaction: 'tx_tag_mergercrypto',
  dividendTransaction: 'tx_tag_dividend',
  delistingTransaction: 'tx_tag_delisting',
  withdrawalCryptoTransfersTransaction: 'tx_details_type_withdrawal',
  depositCryptoTransfersTransaction: 'deposit',
  titleCategoryBuy: 'tx_list_buy_item',
  titleCategorySell: 'tx_list_sell_item',
  titleCategoryGiveaway: 'tx_list_giveaway_item',
  titleCategoryCorporateAction: 'tx_list_corporate_action_item',
  titleCategoryCryptoAction: 'tx_list_crypto_action_item',
  titleCategoryStorageFee: 'tx_list_storage_fee_item',
  titleCategoryStackingReward: 'tx_list_staking_reward_item',
  titleCategoryStaked: 'tx_list_stake_item',
  titleCategoryUnstaked: 'tx_list_unstake_item',
  titleCategoryTaxRefund: 'tx_list_tax_refund_item',
  titleCategoryWithdrawal: 'tx_list_withdrawal_item',
  titleCategoryRefund: 'tx_list_refund_item',
  titleCategoryDeposit: 'tx_list_deposit_item',
  titleCategoryOtc: 'tx_list_otc_item',
  titleCategorySavingsPlan: 'tx_list_savings_plan_item',
  titleCategorySwap: 'tx_list_swap_item',
  grams: 'grams',
  confirmed: 'confirmed',
  failed: 'failed',
  finished: 'finished',
  scheduled: 'scheduled',
  savingsPlan: 'savings_plan',
} as const;

type TxValueKeys =
  (typeof transactionItemTranslations)[keyof typeof transactionItemTranslations];

export const transactionItemInvertedTranslations: Record<
  TxValueKeys,
  keyof typeof transactionItemTranslations
> = {
  tx_tag_otc_buy: 'otcTransaction',
  tx_tag_spinoff: 'spinOffTransaction',
  tx_tag_stocksplit: 'stockSplitTransaction',
  tx_tag_reversestocksplit: 'reverseStockSplitTransaction',
  tx_tag_mergerstock: 'mergerStockTransaction',
  tx_tag_mergercash: 'mergerCashTransaction',
  tx_tag_mergercrypto: 'mergerCryptoTransaction',
  tx_tag_dividend: 'dividendTransaction',
  tx_tag_delisting: 'delistingTransaction',
  tx_details_type_withdrawal: 'withdrawalCryptoTransfersTransaction',
  deposit: 'depositCryptoTransfersTransaction',
  tx_list_buy_item: 'titleCategoryBuy',
  tx_list_sell_item: 'titleCategorySell',
  tx_list_giveaway_item: 'titleCategoryGiveaway',
  tx_list_corporate_action_item: 'titleCategoryCorporateAction',
  tx_list_crypto_action_item: 'titleCategoryCryptoAction',
  tx_list_storage_fee_item: 'titleCategoryStorageFee',
  tx_list_staking_reward_item: 'titleCategoryStackingReward',
  tx_list_stake_item: 'titleCategoryStaked',
  tx_list_unstake_item: 'titleCategoryUnstaked',
  tx_list_tax_refund_item: 'titleCategoryTaxRefund',
  tx_list_withdrawal_item: 'titleCategoryWithdrawal',
  tx_list_refund_item: 'titleCategoryRefund',
  tx_list_deposit_item: 'titleCategoryDeposit',
  tx_list_otc_item: 'titleCategoryOtc',
  tx_list_savings_plan_item: 'titleCategorySavingsPlan',
  tx_list_swap_item: 'titleCategorySwap',
  grams: 'grams',
  confirmed: 'confirmed',
  failed: 'failed',
  finished: 'finished',
  scheduled: 'scheduled',
  savings_plan: 'savingsPlan',
};

export type TransactionItemTranslationsKeys = {
  otcTransaction: string;
  spinOffTransaction: string;
  stockSplitTransaction: string;
  reverseStockSplitTransaction: string;
  mergerStockTransaction: string;
  mergerCashTransaction: string;
  mergerCryptoTransaction: string;
  dividendTransaction: string;
  delistingTransaction: string;
  withdrawalCryptoTransfersTransaction: string;
  depositCryptoTransfersTransaction: string;
  titleCategoryBuy: string;
  titleCategorySell: string;
  titleCategoryGiveaway: string;
  titleCategoryCorporateAction: string;
  titleCategoryCryptoAction: string;
  titleCategoryStorageFee: string;
  titleCategoryStackingReward: string;
  titleCategoryStaked: string;
  titleCategoryUnstaked: string;
  titleCategoryTaxRefund: string;
  titleCategoryWithdrawal: string;
  titleCategoryDeposit: string;
  titleCategoryOtc: string;
  titleCategorySavingsPlan: string;
  titleCategorySwap: string;
  grams: string;
  confirmed: string;
  failed: string;
  finished: string;
  scheduled: string;
  savingsPlan: string;
};

export const getTransactionTranslations = (
  t: (translationKey: keyof TransactionItemTranslationsKeys) => string,
): TransactionItemTranslationsKeys => {
  return Object.entries(transactionItemTranslations).reduce(
    (acc, [key, value]) => {
      acc[key as keyof TransactionItemTranslationsKeys] = t(
        value as keyof TransactionItemTranslationsKeys,
      );

      return acc;
    },
    {} as TransactionItemTranslationsKeys,
  );
};

enum FrontendTitleTransactionCategories {
  BUY = 'tx_buy_item',
  SELL = 'tx_sell_item',
  GIVEAWAY = 'tx_giveaway_item',
  CORPORATE_ACTION = 'tx_corporate_action_item',
  CRYPTO_ACTION = 'tx_crypto_action_item',
  STORAGE_FEE = 'tx_storage_fee_item',
  STAKING_REWARD = 'tx_staking_reward_item',
  TAX_REFUND = 'tx_tax_refund_item',
  WITHDRAWAL = 'tx_withdrawal_item',
  DEPOSIT = 'tx_deposit_item',
  OTC = 'tx_otc_item',
  SAVINGS_PLAN = 'tx_savings_plan_item',
  SWAP = 'tx_swap_item',
  STAKE = 'staking',
  UNSTAKE = 'unstaking',
  ORDER_BUY = 'tx_list_buy_item',
  ORDER_SELL = 'tx_list_sell_item',
}

enum TransactionListTitleCategories {
  BUY = 'tx_list_buy_item',
  SELL = 'tx_list_sell_item',
  GIVEAWAY = 'tx_list_giveaway_item',
  CORPORATE_ACTION = 'tx_list_corporate_action_item',
  CRYPTO_ACTION = 'tx_list_crypto_action_item',
  STORAGE_FEE = 'tx_list_storage_fee_item',
  STAKING_REWARD = 'tx_list_staking_reward_item',
  TAX_REFUND = 'tx_list_tax_refund_item',
  WITHDRAWAL = 'tx_list_withdrawal_item',
  REFUND = 'tx_list_refund_item',
  DEPOSIT = 'tx_list_deposit_item',
  OTC = 'tx_list_otc_item',
  SAVINGS_PLAN = 'tx_list_savings_plan_item',
  SWAP = 'tx_list_swap_item',
  STAKE = 'tx_list_stake_item',
  UNSTAKE = 'tx_list_unstake_item',
}

export enum TransactionDirection {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
}

export const transactionTypesWithDetail: string[] = [
  transactionTypeByTypeName.BuyTransaction,
  transactionTypeByTypeName.SellTransaction,
  transactionTypeByTypeName.SavingsPlanTransaction,
  transactionTypeByTypeName.WithdrawalTransaction,
  transactionTypeByTypeName.DepositTransaction,
  transactionTypeByTypeName.InternalSavingsPlanTransaction,
  transactionTypeByTypeName.WithdrawalCryptoTransfersTransaction,
  transactionTypeByTypeName.DepositCryptoTransfersTransaction,
  transactionTypeByTypeName.DepositTransaction,
  transactionTypeByTypeName.DepositTransactionFiatTransfer,
  transactionTypeByTypeName.SwapTransaction,
  transactionTypeByTypeName.GiveawayTransaction,
  transactionTypeByTypeName.MetalStorageFeeTransaction,
  transactionTypeByTypeName.StakingRewardTransaction,
  transactionTypeByTypeName.StakeStakingTransaction,
  transactionTypeByTypeName.UnstakeStakingTransaction,
  transactionTypeByTypeName.TaxRefundTransaction,
  transactionTypeByTypeName.OtcTransaction,
  transactionTypeByTypeName.MergerCryptoTransaction,
  transactionTypeByTypeName.DelistingTransaction,
  transactionTypeByTypeName.DividendTransaction,
  transactionTypeByTypeName.MergerCashTransaction,
  transactionTypeByTypeName.MergerStockTransaction,
  transactionTypeByTypeName.RightsIssueTransaction,
  transactionTypeByTypeName.StockSplitTransaction,
  transactionTypeByTypeName.ReverseStockSplitTransaction,
  transactionTypeByTypeName.SpinOffTransaction,
  transactionTypeByTypeName.BuyOrderTransaction,
  transactionTypeByTypeName.SellOrderTransaction,
];

export const getTransactionTranslationKeywordByType = (
  type: TransactionTypes,
) => {
  switch (true) {
    case type === 'BUY':
      return FrontendTitleTransactionCategories.BUY;

    case type === 'SELL':
      return FrontendTitleTransactionCategories.SELL;

    case type === 'GIVEAWAY':
      return FrontendTitleTransactionCategories.GIVEAWAY;

    case type === 'INTERNAL_SAVINGS_PLAN':
      return FrontendTitleTransactionCategories.SAVINGS_PLAN;

    case type === 'SAVINGS_PLAN':
      return FrontendTitleTransactionCategories.SAVINGS_PLAN;

    case type === 'STAKING_REWARD':
      return FrontendTitleTransactionCategories.STAKING_REWARD;

    case type === 'DIVIDEND':
      return FrontendTitleTransactionCategories.CORPORATE_ACTION;

    case type === 'TAX_REFUND':
      return FrontendTitleTransactionCategories.TAX_REFUND;

    case type === 'DEDUCTION':
      // MISSING TRANSLATION IN SOURCE FOR THIS: https://docs.google.com/spreadsheets/d/12TVXMsmIp-oQy50hP69QOs6uU2h2_KP9RaM9TFqer0k/edit?gid=1367121438#gid=1367121438
      return FrontendTitleTransactionCategories.CORPORATE_ACTION;

    case type === 'METAL_STORAGE_FEE':
      return FrontendTitleTransactionCategories.STORAGE_FEE;

    case type === 'OTC':
      return FrontendTitleTransactionCategories.OTC;

    case type === 'WITHDRAWAL':
      return FrontendTitleTransactionCategories.WITHDRAWAL;

    case type === 'DEPOSIT':
      return FrontendTitleTransactionCategories.DEPOSIT;

    case type === 'SWAP':
      return FrontendTitleTransactionCategories.SWAP;

    case type === 'DELISTING':
      return FrontendTitleTransactionCategories.CORPORATE_ACTION;

    case type === 'MERGER_CRYPTO':
      return FrontendTitleTransactionCategories.CRYPTO_ACTION;

    case type === 'MERGER_CASH':
      return FrontendTitleTransactionCategories.CORPORATE_ACTION;

    case type === 'MERGER_STOCK':
      return FrontendTitleTransactionCategories.CORPORATE_ACTION;

    case type === 'REVERSE_STOCK_SPLIT':
      return FrontendTitleTransactionCategories.CORPORATE_ACTION;

    case type === 'SPIN_OFF':
      return FrontendTitleTransactionCategories.CORPORATE_ACTION;

    case type === 'STOCK_SPLIT':
      return FrontendTitleTransactionCategories.CORPORATE_ACTION;

    case type === 'WITHDRAWAL_CRYPTO_TRANSFERS':
      return FrontendTitleTransactionCategories.WITHDRAWAL;

    case type === 'DEPOSIT_CRYPTO_TRANSFERS':
      return FrontendTitleTransactionCategories.DEPOSIT;

    case type === 'STAKE':
      return FrontendTitleTransactionCategories.STAKE;

    case type === 'UNSTAKE':
      return FrontendTitleTransactionCategories.UNSTAKE;

    case type === 'ORDER_BUY':
      return FrontendTitleTransactionCategories.ORDER_BUY;

    case type === 'ORDER_SELL':
      return FrontendTitleTransactionCategories.ORDER_SELL;

    default:
      return FrontendTitleTransactionCategories.CORPORATE_ACTION;
  }
};

export const getTransactionListTitleTranslationKeywordByType = (
  type: TransactionTypes,
) => {
  switch (true) {
    case type === 'BUY':
      return TransactionListTitleCategories.BUY;

    case type === 'SELL':
      return TransactionListTitleCategories.SELL;

    case type === 'GIVEAWAY':
      return TransactionListTitleCategories.GIVEAWAY;

    case type === 'SAVINGS_PLAN':
      return TransactionListTitleCategories.SAVINGS_PLAN;

    case type === 'STAKING_REWARD':
      return TransactionListTitleCategories.STAKING_REWARD;

    case type === 'DIVIDEND':
      return TransactionListTitleCategories.CORPORATE_ACTION;

    case type === 'TAX_REFUND':
      return TransactionListTitleCategories.TAX_REFUND;

    case type === 'DEDUCTION':
      // MISSING TRANSLATION IN SOURCE FOR THIS: https://docs.google.com/spreadsheets/d/12TVXMsmIp-oQy50hP69QOs6uU2h2_KP9RaM9TFqer0k/edit?gid=1367121438#gid=1367121438
      return TransactionListTitleCategories.CORPORATE_ACTION;

    case type === 'METAL_STORAGE_FEE':
      return TransactionListTitleCategories.STORAGE_FEE;

    case type === 'OTC':
      return TransactionListTitleCategories.OTC;

    case type === 'WITHDRAWAL':
      return TransactionListTitleCategories.WITHDRAWAL;

    case type === 'DEPOSIT':
      return TransactionListTitleCategories.DEPOSIT;

    case type === 'SWAP':
      return TransactionListTitleCategories.SWAP;

    case type === 'DELISTING':
      return TransactionListTitleCategories.CORPORATE_ACTION;

    case type === 'MERGER_CRYPTO':
      return TransactionListTitleCategories.CRYPTO_ACTION;

    case type === 'MERGER_CASH':
      return TransactionListTitleCategories.CORPORATE_ACTION;

    case type === 'MERGER_STOCK':
      return TransactionListTitleCategories.CORPORATE_ACTION;

    case type === 'REVERSE_STOCK_SPLIT':
      return TransactionListTitleCategories.CORPORATE_ACTION;

    case type === 'SPIN_OFF':
      return TransactionListTitleCategories.CORPORATE_ACTION;

    case type === 'STOCK_SPLIT':
      return TransactionListTitleCategories.CORPORATE_ACTION;

    case type === 'WITHDRAWAL_CRYPTO_TRANSFERS':
      return TransactionListTitleCategories.WITHDRAWAL;

    case type === 'DEPOSIT_CRYPTO_TRANSFERS':
      return TransactionListTitleCategories.DEPOSIT;

    case type === 'ORDER_BUY':
      return TransactionListTitleCategories.BUY;

    case type === 'ORDER_SELL':
      return TransactionListTitleCategories.SELL;

    case type === 'STAKE':
      return TransactionListTitleCategories.STAKE;

    case type === 'UNSTAKE':
      return TransactionListTitleCategories.UNSTAKE;

    case type === 'REFUND':
      return TransactionListTitleCategories.REFUND;

    default:
      return TransactionListTitleCategories.CORPORATE_ACTION;
  }
};

export const isCryptoTransferTransaction = (
  type: TransactionTypes | NotificationType,
) => {
  return [
    transactionTypeByTypeName.DepositTransaction,
    transactionTypeByTypeName.WithdrawalTransaction,
    transactionTypeByTypeName.DepositCryptoTransfersTransaction,
    transactionTypeByTypeName.WithdrawalCryptoTransfersTransaction,
    transactionTypeByTypeName.WithdrawalRefundTransaction,
  ].some(
    (item) =>
      item === type ||
      item === transactionTypeByNotificationType[type as NotificationType],
  );
};

export const isNUITransaction = (transactionType: TransactionTypes) => {
  return [
    transactionTypeByTypeName.GiveawayTransaction,
    transactionTypeByTypeName.MetalStorageFeeTransaction,
    transactionTypeByTypeName.StakingRewardTransaction,
    transactionTypeByTypeName.TaxRefundTransaction,
  ].some((item) => item === transactionType);
};

export const isFiatTransaction = (transactionType: TransactionTypes) => {
  return [
    transactionTypeByTypeName.DepositTransactionFiatTransfer,
    transactionTypeByTypeName.WithdrawalTransactionFiatTransfer,
  ].some((item) => item === transactionType);
};

export const isSwapTransaction = (transactionType: string) =>
  ([transactionTypeByTypeName.SwapTransaction] as string[]).includes(
    transactionType,
  );

export const isStakingTransaction = (transactionType: string) =>
  (
    [
      transactionTypeByTypeName.StakeStakingTransaction,
      transactionTypeByTypeName.UnstakeStakingTransaction,
    ] as string[]
  ).includes(transactionType);

export const isCryptoActionTransaction = (transactionType: string) =>
  ([transactionTypeByTypeName.MergerCryptoTransaction] as string[]).includes(
    transactionType,
  );

export const isCorporateActionTransaction = (
  transactionType: TransactionTypes,
) => {
  return [
    transactionTypeByTypeName.DeductionTransaction,
    transactionTypeByTypeName.DelistingTransaction,
    transactionTypeByTypeName.DividendTransaction,
    transactionTypeByTypeName.MergerCashTransaction,
    transactionTypeByTypeName.MergerStockTransaction,
    transactionTypeByTypeName.RightsIssueTransaction,
    transactionTypeByTypeName.StockSplitTransaction,
    transactionTypeByTypeName.ReverseStockSplitTransaction,
    transactionTypeByTypeName.SpinOffTransaction,
    transactionTypeByTypeName.TenderTransaction,
  ].some((item) => item === transactionType);
};

export const isClassicTransaction = (transactionType: string) => {
  return [
    transactionTypeByTypeName.BuyTransaction,
    transactionTypeByTypeName.SellTransaction,
    transactionTypeByTypeName.SavingsPlanTransaction,
    transactionTypeByTypeName.InternalSavingsPlanTransaction,
    transactionTypeByTypeName.OtcTransaction,
  ].some((item) => item === transactionType);
};

export const isWithdrawalTransaction = (transactionType: string) => {
  return [
    transactionTypeByTypeName.WithdrawalTransaction,
    transactionTypeByTypeName.WithdrawalCryptoTransfersTransaction,
  ].some((item) => item === transactionType);
};

export const isDepositTransaction = (transactionType: string) => {
  return [
    transactionTypeByTypeName.DepositTransaction,
    transactionTypeByTypeName.DepositCryptoTransfersTransaction,
  ].some((item) => item === transactionType);
};

export const isSavingsPlanTransaction = (transactionType: string) => {
  return [
    transactionTypeByTypeName.SavingsPlanTransaction,
    transactionTypeByTypeName.InternalSavingsPlanTransaction,
  ].some((item) => item === transactionType);
};

export const isFailedSavingsPlanTransaction = (transactionType: string) => {
  return [
    transactionTypeByTypeName.SavingsPlanFailedMergerCrypto,
    transactionTypeByTypeName.SavingsPlanFailedMergerStock,
    transactionTypeByTypeName.SavingsPlanFailedDelisting,
    transactionTypeByTypeName.SavingsPlanFailedMergerCash,
    transactionTypeByTypeName.SavingsPlanFailedInPartnerApp,
  ].some((item) => item === transactionType);
};

export const getPartialTransactionEntryType = (
  typename: string,
  transactionId: string,
  assetName: string,
): TransactionEntryType => {
  return {
    __typename: typename,
    id: transactionId,
    parentId: transactionId,
    asset: {
      __typename: '',
      id: '',
      name: assetName,
      logoUrl: null,
      symbol: null,
    },
    assetAmount: null,
    initiatedBy: null,
    category: 'CRYPTO_TRANSFER',
    direction: null,
    fiatAmount: null,
    status: null,
    time: null,
  };
};
